import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { arrayToObject, take } from 'lib/array/arrayUtils'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import {
  getMultiRoomCalendarDays,
  getMultiRoomDaysOtherAvailability,
  getSelectedOfferPageMultiRoomDay,
} from 'selectors/offerPage/multipleRoomBookingSelectors'
import type { MonthDayView } from 'selectors/offerPage/offerPageSelectors'
import {
  getCalendarDays,
  getDaysOtherAvailability,
  getFixedFlashLengthOfStayCheckInCalendarDays,
  getFixedFlashLengthOfStayCalendarDays,
  getFixedFlashLengthOfStayCheckoutCalendarDays,
  getFixedFlashLengthOfStayMaximumDuration,
  getSelectedOfferPageDay,
  getSelectedRoomRateId,
} from 'selectors/offerPage/offerPageSelectors'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import OfferCalendarSidebarPriceBlurb from './OfferCalendarSidebarPriceBlurb'
import useMountedEffect from 'hooks/useMountedEffect'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'LuxKit/TextLink'
import OfferCalendarSidebarDaysMonth from './OfferCalendarSidebarDaysMonth'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import type { TrackableProduct } from 'components/OfferList/OfferListEventsContext'
import { trackCalendarMonthImpression } from 'analytics/calendarMonthImpression'
import CalendarMonthPriceBlockImpression from '../CalendarMonthPriceBlockImpression'
import { INTL_LONG_MONTH } from 'constants/dateFormats'
import { type CalendarMonthImpressionPrice, getCalendarMonthImpressionPrice } from '../calendarMonthImpressionPrice'
import { EmptyObject } from 'lib/object/objectUtils'

const Calendar = styled.div`
  animation: ${fadeIn} 0.2s;
`

interface Props {
  daysByMonth: Array<MonthDayView>
  className?: string
  onBackToMonth?: () => void
  initialMonth?: string
  otherAvailability: { [checkIn: string]: boolean }
  selectedDay?: App.CalendarDay
  flightsOnly?: boolean
  contentRef: React.RefObject<HTMLDivElement | null>
  isAnyFlightsActive?: boolean
  bundleOfferId?: string
  roomCount: number
  displayMemberPrice: boolean
  offer?: TrackableProduct
  roomRateId?: string
  selectedCheckOut?: string
  selectedCheckIn?: string
  checkoutCalendarDays: Record<string, App.CalendarDay>
  checkInCalendarDays: Record<string, App.CalendarDay>
  maximumDuration?: number
  isFixedFlashLengthOfStayEnabled?: boolean
}

function OfferCalendarSidebarDayPicker(props: Props) {
  const {
    daysByMonth,
    className,
    initialMonth,
    otherAvailability,
    selectedDay,
    flightsOnly,
    contentRef,
    isAnyFlightsActive,
    onBackToMonth,
    bundleOfferId,
    roomCount,
    displayMemberPrice,
    offer,
    roomRateId,
    selectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDays,
    checkInCalendarDays,
    maximumDuration,
    isFixedFlashLengthOfStayEnabled = false,
  } = props
  const intl = useIntl()
  // always show month selected, previous month and next month
  const initialMonthIndex = useRef(daysByMonth.findIndex((entry) => entry[1][0]?.monthKey === initialMonth)).current
  const [monthsOnDisplay, setMonthsOnDisplay] = useState({ toShow: 3, offset: Math.max(initialMonthIndex - 1, 0) })
  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement | null>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)
  const trackedMonthKeys = useRef(new Set<string>())
  const handleMonthImpression = useCallback((monthKey: string, monthNumber: number, year: number, impressionPrice?: CalendarMonthImpressionPrice) => {
    const dedupKey = roomRateId ? `${monthKey}:${roomRateId}` : monthKey
    if (trackedMonthKeys.current.has(dedupKey)) return
    trackedMonthKeys.current.add(dedupKey)
    trackCalendarMonthImpression({
      lastInteractedPriceMember: impressionPrice?.memberPrice,
      lastInteractedPriceNonMember: impressionPrice?.nonMemberPrice,
      monthNumber,
      offer,
      roomRateId,
      year,
    })
  }, [offer, roomRateId])

  // We discard the initial 'previous in view' check because we already show
  // an extra month before this one on mount
  useMountedEffect(() => {
    if (previousInView) {
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  // mounting layout handles transition from month to day calendar
  useEffect(() => {
    if (initialMonthIndex > 0) {
      // we need to scroll to the 'middle' month
      const month = daysByMonth[monthsOnDisplay.offset]
      const element = monthElements.current[month[1][0].monthKey]
      if (element) {
        const scrollPosition = element.offsetHeight + element.offsetTop
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  // on offset change handles when scrolling up and new month appears
  useEffect(() => {
    if (scrollRestoreMonth.current !== undefined) {
      const month = daysByMonth[monthsOnDisplay.offset]
      const element = monthElements.current[month[1][0].monthKey]
      if (element) {
        const scrollPosition = element.offsetHeight + element.offsetTop + 40
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextInView) {
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  const monthData = useMemo(() => {
    return arrayToObject(daysByMonth, ([title]) => title, ([_, days]) => {
      const withTotals = days.filter(day => {
        if (flightsOnly && !day.flightCacheActive) {
          // we don't have a price to show if flight cache isn't active and we're only showing flights
          return false
        }
        return isAnyFlightsActive ? day.flightsAvailable && day.hotelAvailable : day.hotelAvailable
      })
      const hotelFromPrice = withTotals.length ? Math.min(...withTotals.map(d => displayMemberPrice ? d.hotelMemberTotal : d.hotelTotal)) : 0
      const packagedFromPrice = withTotals.length ? Math.min(...withTotals.map(d => displayMemberPrice ? d.packagedMemberTotal : d.packagedTotal)) : 0
      const hotelImpressionPrice = getCalendarMonthImpressionPrice(withTotals, false, displayMemberPrice)
      const packagedImpressionPrice = getCalendarMonthImpressionPrice(withTotals, true, displayMemberPrice)

      return {
        hotelFromPrice,
        hotelImpressionPrice,
        packagedFromPrice,
        packagedImpressionPrice,
        hasSpecial: days.some(day => (day.dealId || day.hasTactical) && day.hotelAvailable),
      } })
  }, [daysByMonth, displayMemberPrice, flightsOnly, isAnyFlightsActive])
  const fixedFlashCheckoutCalendarDays = isFixedFlashLengthOfStayEnabled ? checkoutCalendarDays : EmptyObject
  const fixedFlashCheckInCalendarDays = isFixedFlashLengthOfStayEnabled ? checkInCalendarDays : EmptyObject
  const fixedFlashMaximumDuration = isFixedFlashLengthOfStayEnabled ? maximumDuration : undefined

  return (
    <VerticalSpacer className={className} gap={20}>
      {isAnyFlightsActive && <Caption variant="medium" colour="light-foreground-alt">
        {intl.formatMessage(
          {
            id: 'h4SBsWO3Y-Hidjkixk8v',
            defaultMessage: '*Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {brandName} full Terms and Conditions apply',
            description: 'Disclaimer about indicative flight pricing on the calendar sidebar',
          },
          { brandName: config.brandConfig.name },
        )}
      </Caption>}
      {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
      {take(daysByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map(([title, days, { year, monthIndex }]) => {
        const fromPrice = isAnyFlightsActive ? monthData[title].packagedFromPrice : monthData[title].hotelFromPrice
        const impressionPrice = isAnyFlightsActive ? monthData[title].packagedImpressionPrice : monthData[title].hotelImpressionPrice
        const monthTitle = intl.formatDate(new Date(year, monthIndex), INTL_LONG_MONTH)

        return <Calendar key={title} ref={(el) => { monthElements.current[days[0].monthKey] = el }}>
          <VerticalSpacer gap={8}>
            <VerticalSpacer gap={4}>
              <Group direction="horizontal" horizontalAlign="space-between">
                <Heading variant="heading5">{monthTitle}</Heading>
                <TextLink size="large" onClick={onBackToMonth}>{days[0].year}</TextLink>
              </Group>
              {!!fromPrice && !bundleOfferId && <OfferCalendarSidebarPriceBlurb
                fromPrice={fromPrice}
                roomCount={roomCount}
                special={monthData[title].hasSpecial}
                hasLoyaltyRoomUpgrade={days.some(day => !!day.loyalty.availableUpgradeTargetsUniqueKeys.room || !!day.loyalty.availableUpgradeTargetsUniqueKeys.package)}
              />}
            </VerticalSpacer>
            <CalendarMonthPriceBlockImpression
              scrollRoot={contentRef.current}
              monthKey={days[0].monthKey}
              monthNumber={monthIndex + 1}
              year={year}
              shouldTrack={Number.isFinite(fromPrice) && fromPrice > 0}
              impressionPrice={impressionPrice}
              onImpression={handleMonthImpression}
            >
              <OfferCalendarSidebarDaysMonth
                days={days}
                cheapestPrice={fromPrice}
                selectedDay={selectedDay}
                selectedCheckIn={selectedCheckIn}
                selectedCheckOut={selectedCheckOut}
                checkoutCalendarDays={fixedFlashCheckoutCalendarDays}
                checkInCalendarDays={fixedFlashCheckInCalendarDays}
                maximumDuration={fixedFlashMaximumDuration}
                otherAvailability={otherAvailability}
                isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
              />
            </CalendarMonthPriceBlockImpression>
          </VerticalSpacer>
        </Calendar>
      })}
      {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < daysByMonth.length && <div ref={nextRef} />}
    </VerticalSpacer>
  )
}

export default withOfferPageAndReduxState((currentState, appState, props: Props) => {
  const state = takeCurrentOfferState(currentState)
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  const shouldUseFixedFlashLengthOfStaySelectors = !!props.isFixedFlashLengthOfStayEnabled && !isMultiRoom
  let daysByMonth: Array<MonthDayView>
  if (isMultiRoom) {
    daysByMonth = getMultiRoomCalendarDays(state, appState)
  } else if (shouldUseFixedFlashLengthOfStaySelectors) {
    daysByMonth = getFixedFlashLengthOfStayCalendarDays(appState, state)
  } else {
    daysByMonth = getCalendarDays(appState, state)
  }

  return {
    bundleOfferId: state.bundleOffer?.id,
    selectedCheckIn: state.checkInDate,
    selectedCheckOut: state.checkOutDate,
    selectedDay: isMultiRoom ? getSelectedOfferPageMultiRoomDay(state, appState) : getSelectedOfferPageDay(appState, state),
    checkoutCalendarDays: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayCheckoutCalendarDays(state, appState) : EmptyObject,
    checkInCalendarDays: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayCheckInCalendarDays(state, appState) : EmptyObject,
    maximumDuration: shouldUseFixedFlashLengthOfStaySelectors ? getFixedFlashLengthOfStayMaximumDuration(state, appState) : undefined,
    daysByMonth,
    otherAvailability: isMultiRoom ? getMultiRoomDaysOtherAvailability(state, appState) : getDaysOtherAvailability(appState, state),
    isAnyFlightsActive: state.flights,
    roomCount: state.rooms?.length ?? 1,
    displayMemberPrice: currentState.displayMemberPrice,
    offer: state.offer as TrackableProduct | undefined,
    roomRateId: getSelectedRoomRateId(state),
  }
}, OfferCalendarSidebarDayPicker)
